<template>
  <div
    class="zm-multi-select-dropdown zm-popper"
    tabindex="0"
    role="dialog"
    aria-label="multi select dropdown"
    @keydown.esc="$emit('apply-close')"
  >
    <header v-if="filterAllVisible || searchVisible">
      <zm-input
        v-if="searchVisible"
        v-model="filterText"
        class="filter-text"
        size="small"
        :label="searchPlaceholder"
        :placeholder="searchPlaceholder"
        prefix-icon="zm-icon-search"
        @keydown.enter.native="triggerRemoteSearch"
        @blur="triggerRemoteSearch"
      />
      <zm-checkbox
        v-if="filterText.trim().length === 0"
        v-model="isSelectAll"
        :indeterminate="isIndeterminate"
        :label="filterAllLabel"
        :controls="options.map(item => `zm-ms-${item[valueKey]}`).join(' ')"
        @change="handleCheckAllChange"
      >
        {{ filterAllLabel }}
      </zm-checkbox>
      <slot name="header" />
    </header>
    <zm-scrollbar
      ref="scrollbar"
      wrap-style="min-height: 70px;max-height: 200px;"
      @scroll-end="triggerLoadData"
    >
      <zm-tree
        ref="tree"
        :data="screenOptions"
        show-checkbox
        :default-expand-all="defaultExpandAll"
        :node-key="valueKey"
        :props="{
          children: childKey,
          label: nameKey,
          disabled: disabledKey,
        }"
        @check-change="handleCheckChange"
      >
        <template slot-scope="props">
          <slot
            name="option"
            :item="props.data"
          />
        </template>
      </zm-tree>
      <div
        v-if="hasMore"
        v-loading="loading"
        class="loading-tips"
      />
    </zm-scrollbar>
    <footer v-if="$slots.footer || buttonVisible">
      <slot name="footer" />
      <div v-if="buttonVisible" class="button-footer">
        <span v-if="$slots.applyButton" @click="submitSelected">
          <slot name="applyButton" />
        </span>
        <zm-button v-else @click="submitSelected">
          {{ confirmLabel }}
        </zm-button>

        <template v-if="clearVisible">
          <span v-if="$slots.clearButton" @click="clearSelect">
            <slot name="clearButton" />
          </span>
          <zm-button
            v-else
            type="plain"
            ghost
            @click="clearSelect"
          >
            {{ clearLabel }}
          </zm-button>
        </template>
      </div>
    </footer>
  </div>
</template>

<script>
  import { debounce } from 'throttle-debounce';
  import isEqual from 'lodash-es/isEqual';
  import Popper from '../../utils/vue-popper';
  import AriaDialog from '../../utils/aria-dialog';

  export default {
    name: 'ZmMultiSelectDropdown',
    mixins: [Popper],
    props: {
      placement: {
        type: String,
        default: 'bottom-start'
      },
      initSelect: {
        type: Array,
        default() {
          return [];
        }
      },
      options: {
        type: Array,
        default() {
          return [];
        }
      },
      valueKey: {
        type: String,
        default: ''
      },
      nameKey: {
        type: String,
        default: ''
      },
      childKey: {
        type: String,
        default: 'children'
      },
      disabledKey: {
        type: String,
        default: 'disabled'
      },
      clearVisible: {
        type: Boolean,
        default: true
      },
      searchVisible: {
        type: Boolean,
        default: true
      },
      remoteSearch: Boolean,
      buttonVisible: {
        type: Boolean,
        default: true
      },
      filterAllVisible: {
        type: Boolean,
        default: true
      },
      searchPlaceholder: {
        type: String,
        default: ''
      },
      filterAllLabel: {
        type: String,
        default: ''
      },
      confirmLabel: {
        type: String,
        default: ''
      },
      clearLabel: {
        type: String,
        default: ''
      },
      defaultExpandAll: {
        type: Boolean,
        default: false
      },
      hasMore: Boolean,
      loading: Boolean
    },
    data() {
      return {
        filterText: '',
        isSelectAll: false,
        selectedIdList: [],
        isIndeterminate: false
      };
    },
    computed: {
      screenOptions() {
        return this.options;
        // return this.getFilteredOptions();
      }
    },
    watch: {
      options: {
        handler() {
          this.debounceCheckLoadMore();
          this.debounceCheckSelectAll();
        },
        immediate: true,
        deep: true
      }
    },
    beforeCreate() {
      this.debounceCheckLoadMore = debounce(20, () => {
        const scrollbarRef = this.$refs.scrollbar;
        if (!scrollbarRef) {
          return;
        }
        // check if no scrollbar
        if (scrollbarRef.wrap.scrollHeight === scrollbarRef.wrap.clientHeight) {
          this.triggerLoadData();
        }
        // trigger scrollbar update
        scrollbarRef && scrollbarRef.update();
      });
      this.debounceCheckSelectAll = debounce(30, () => {
        const selectedValue = this.$refs.tree.getCheckedNodes(false, false, true);
        if (selectedValue.length === 0 || this.options.length === 0) {
          this.isSelectAll = false;
          this.isIndeterminate = false;
        } else {
          const isSelectedAll = isEqual(selectedValue, this.options);
          this.isSelectAll = isSelectedAll;
          this.isIndeterminate = !isSelectedAll;
        }
      });
    },
    mounted() {
      this.$refs.tree.setCheckedNodes(this.initSelect);
      this.debounceCheckSelectAll();
      this.initPopper();
    },
    beforeDestroy() {
      this.destroyPopper();
    },
    methods: {
      initAriaDialog() {
        const focusAfterClosed = document.activeElement;
        const firstFocus = this.getFirstFocus();
        this.ariaDialog = new AriaDialog(this.$el, focusAfterClosed, firstFocus);
      },
      closeAriaDialog() {
        this.ariaDialog && this.ariaDialog.closeDialog();
        this.ariaDialog = null;
      },
      getFirstFocus() {
        const focusFirst = this.$el.querySelector('input');
        if (focusFirst) {
          return focusFirst;
        }
        return null;
      },
      getFilteredOptions() {
        if (this.remoteSearch) {
          return this.options;
        }
        const filterText = (this.filterText || '').trim().toLowerCase();
        if (filterText.length === 0) {
          return this.options;
        }
        return this.options.filter(item => {
          const label = (item[this.nameKey] || '').toLowerCase();
          return label.indexOf(filterText) > -1;
        });
      },
      initPopper() {
        this.referenceElm = this.$parent.$el;
        this.$parent.popperElm = this.popperElm = this.$el;
        this.updatePopper();
      },
      handleCheckAllChange(val) {
        this.$refs.tree.setCheckedNodes(val ? this.options : []);
        this.submitIfButtonInvisible();
      },
      handleCheckChange() {
        this.debounceCheckSelectAll();
        this.submitIfButtonInvisible();
      },
      handleSelectedChange(value) {
        const checkedCount = value.length;
        this.isSelectAll = checkedCount === this.options.length;
        this.isIndeterminate = checkedCount > 0 && checkedCount < this.options.length;
        this.submitIfButtonInvisible();
      },
      triggerRemoteSearch() {
        if (this.remoteSearch) {
          // stop remote search when filter string did not changed
          const filterText = this.filterText.trim();
          if (filterText === (this._lastFilterText || '')) {
            return;
          }
          this._lastFilterText = filterText;
          this.triggerLoadData();
        }
      },
      triggerLoadData() {
        let searchText = '';
        if (this.remoteSearch) {
          searchText = this.filterText.trim();
        }
        this.$emit('load-data', searchText);
      },
      clearSelect() {
        this.$emit('data-change', []);
        this.$emit('apply-close');
      },
      submitIfButtonInvisible() {
        if (!this.buttonVisible) {
          const selectedValue = this.$refs.tree.getCheckedNodes(false, false, true);
          this.$emit('data-change', selectedValue);
        }
      },
      submitSelected() {
        const selectedValue = this.$refs.tree.getCheckedNodes(false, false, true);
        this.$emit('data-change', selectedValue);
        this.$emit('apply-close');
      }
    }
  };
</script>

<style lang='scss'>
  @import "../../scss/common/popper";
  @import "../../scss/mixins/mixins";
  @import "../../scss/common/var";
  @include b(multi-select-dropdown) {
    .zm-tree-node__expand-icon {
      width: 18px;
      height: 18px;
      padding: 0 2px;
      font-size: 12px;
    }
  }
</style>
